import React, { useState, useEffect, useRef, useCallback } from 'react';
import { InputText } from 'primereact/inputtext';
import { Button } from 'primereact/button';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Dialog } from 'primereact/dialog';
import { Sidebar } from 'primereact/sidebar';
import { Toast } from 'primereact/toast';
import { BlockUI } from 'primereact/blockui';
import { ProgressSpinner } from 'primereact/progressspinner';
import OrgUnitsService from '../../services/orgUnitsService';
import UserAccountService from '../../services/userAccountService';
import { OrgUnitUser } from '../../models/org-units/orgUnitUser';
import { OrgUnitUserDto, UserAccountDto } from '../../models/user-account';
import { OrganizationalUnit } from '../../models/org-units/orgUnit';
import EditOrgUnitUser from './EditOrgUnitUser';

// Interface for the table display model
interface OrgUnitUserDisplay {
  id: string;
  userId: string;
  email: string;
  firstName: string;
  lastName: string;
  fullName: string;
  roles: string[];
}

interface OrganizationalUnitUsersProps {
  orgUnit: OrganizationalUnit;
  selectedOrgUnit?: OrganizationalUnit;
}

export const OrganizationalUnitUsers: React.FC<OrganizationalUnitUsersProps> = ({ orgUnit, selectedOrgUnit }) => {
  const [users, setUsers] = useState<OrgUnitUser[]>([]);
  const [userDisplayList, setUserDisplayList] = useState<OrgUnitUserDisplay[]>([]);
  const [filteredUserDisplay, setFilteredUserDisplay] = useState<OrgUnitUserDisplay[]>([]);
  const [userSearch, setUserSearch] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [deleteDialogVisible, setDeleteDialogVisible] = useState<boolean>(false);
  const [userToDelete, setUserToDelete] = useState<OrgUnitUserDisplay | null>(null);
  const [userToEdit, setUserToEdit] = useState<OrgUnitUserDto | undefined>(undefined);

  const [sidebarVisible, setSidebarVisible] = useState<boolean>(false);
  const [userEditorSidebarVisible, setUserEditorSidebarVisible] = useState<boolean>(false);
  const [availableUsers, setAvailableUsers] = useState<UserAccountDto[]>([]);
  const [filteredAvailableUsers, setFilteredAvailableUsers] = useState<UserAccountDto[]>([]);
  const [availableUserSearch, setAvailableUserSearch] = useState<string>('');
  const [selectedUsersToAdd, setSelectedUsersToAdd] = useState<UserAccountDto[]>([]);

  const toast = useRef<Toast>(null);
  const orgUnitsService = OrgUnitsService.getInstance();
  const userAccountService = UserAccountService.getInstance();

  const showSuccess = useCallback(
    (message: string) => {
      toast.current?.show({ severity: 'success', summary: 'Success', detail: message, life: 3000 });
    },
    [toast]
  );

  const showError = useCallback(
    (message: string) => {
      toast.current?.show({ severity: 'error', summary: 'Error', detail: message, life: 3000 });
    },
    [toast]
  );

  const loadUsers = useCallback(() => {
    setLoading(true);

    // First get all user accounts to have the full user data
    userAccountService
      .getAll()
      .then((allUserAccounts) => {
        // Then get users for this organization unit
        orgUnitsService
          .getUsers(orgUnit.orgUnitId)
          .then((orgUnitUsers) => {
            setUsers(orgUnitUsers);

            // Create display model by matching org unit users with full user accounts
            const displayUsers = orgUnitUsers.map((orgUnitUser) => {
              const userAccount = allUserAccounts.find((u) => u.id === orgUnitUser.userId);

              return {
                id: orgUnitUser.id || '', // Ensure id is always a string by providing a default empty string
                userId: orgUnitUser.userId,
                email: userAccount?.email || '',
                firstName: userAccount?.firstName || '',
                lastName: userAccount?.lastName || '',
                fullName: `${userAccount?.firstName || ''} ${userAccount?.lastName || ''}`.trim(),
                roles: userAccount?.roles || [],
              };
            });

            // Sort by name
            const sortedDisplayUsers = [...displayUsers].sort((a, b) =>
              a.fullName.toLowerCase().localeCompare(b.fullName.toLowerCase())
            );

            setUserDisplayList(sortedDisplayUsers);
            setFilteredUserDisplay(sortedDisplayUsers);
            setLoading(false);
          })
          .catch((error) => {
            showError(`Error loading users: ${error.message}`);
            setLoading(false);
          });
      })
      .catch((error) => {
        showError(`Error loading user accounts: ${error.message}`);
        setLoading(false);
      });
  }, [orgUnit.orgUnitId, orgUnitsService, userAccountService, showError]);

  const filterDisplayUsers = useCallback(() => {
    if (!userSearch) {
      setFilteredUserDisplay(userDisplayList);
      return;
    }

    const searchTerm = userSearch.toLowerCase();
    const filtered = userDisplayList.filter(
      (user) => user.fullName.toLowerCase().includes(searchTerm) || user.email.toLowerCase().includes(searchTerm)
    );

    setFilteredUserDisplay(filtered);
  }, [userSearch, userDisplayList]);

  const filterAvailableUsers = useCallback(() => {
    if (!availableUserSearch) {
      setFilteredAvailableUsers(availableUsers);
      return;
    }

    const searchTerm = availableUserSearch.toLowerCase();
    const filtered = availableUsers.filter(
      (user) =>
        (user.firstName?.toLowerCase() || '').includes(searchTerm) ||
        (user.lastName?.toLowerCase() || '').includes(searchTerm) ||
        (user.email?.toLowerCase() || '').includes(searchTerm)
    );

    setFilteredAvailableUsers(filtered);
  }, [availableUserSearch, availableUsers]);

  const loadAvailableUsers = useCallback(() => {
    setLoading(true);
    userAccountService
      .getAll()
      .then((allUsers) => {
        // Filter out users already in the org unit
        const existingUserIds = users.map((u) => u.userId);
        const available = allUsers.filter((u) => !existingUserIds.includes(u.id as string));

        // Sort users alphabetically by full name (firstName + lastName)
        const sortedUsers = [...available].sort((a, b) => {
          const nameA = `${a.firstName} ${a.lastName}`.toLowerCase();
          const nameB = `${b.firstName} ${b.lastName}`.toLowerCase();
          return nameA.localeCompare(nameB);
        });

        setAvailableUsers(sortedUsers);
        setFilteredAvailableUsers(sortedUsers);
        setLoading(false);
      })
      .catch((error) => {
        showError(`Error loading available users: ${error.message}`);
        setLoading(false);
      });
  }, [users, userAccountService, showError]);

  const confirmAddUsers = useCallback(() => {
    if (selectedUsersToAdd.length === 0) {
      showError('No users selected');
      return;
    }

    setLoading(true);

    // Create promises for adding each user
    const addPromises = selectedUsersToAdd.map((user) => orgUnitsService.addUser(orgUnit.orgUnitId, user.id as string));

    // Execute all promises
    Promise.all(addPromises)
      .then(() => {
        showSuccess(`${selectedUsersToAdd.length} user(s) added successfully`);
        setSelectedUsersToAdd([]);
        setSidebarVisible(false);
        loadUsers(); // Reload the users list
      })
      .catch((error) => {
        showError(`Error adding users: ${error.message}`);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [selectedUsersToAdd, orgUnit.orgUnitId, orgUnitsService, loadUsers, showError, showSuccess]);

  const handleRemoveUser = useCallback((user: OrgUnitUserDisplay) => {
    setUserToDelete(user);
    setDeleteDialogVisible(true);
  }, []);

  const handleEditUser = useCallback(
    async (user: OrgUnitUserDisplay) => {
      // Get the full user account details directly by ID
      setLoading(true);

      try {
        const userAccount = await userAccountService.get(user.userId);
        const orgUnitUser = await orgUnitsService.getUser(orgUnit.orgUnitId, user.userId);

        const fullUser: OrgUnitUserDto = {
          userAccount,
          losRole: orgUnitUser.losRole as 'LoanOriginator' | 'Processor' | 'Other' | null | undefined,
          orgUnitId: selectedOrgUnit?.orgUnitId || orgUnit.orgUnitId,
        };

        setUserToEdit(fullUser);
        setUserEditorSidebarVisible(true);
      } catch (error: any) {
        showError(`Error loading user details: ${error.message}`);
      } finally {
        setLoading(false);
      }
    },
    [userAccountService, showError, setLoading]
  );

  const confirmRemoveUser = useCallback(() => {
    if (!userToDelete) return;

    setLoading(true);
    orgUnitsService
      .removeUser(orgUnit.orgUnitId, userToDelete.userId)
      .then(() => {
        showSuccess('User removed successfully');
        loadUsers(); // Reload the users list
        setDeleteDialogVisible(false);
      })
      .catch((error) => {
        showError(`Error removing user: ${error.message}`);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [userToDelete, orgUnit.orgUnitId, orgUnitsService, loadUsers, showError, showSuccess]);

  const openAddUserSidebar = useCallback(() => {
    setSelectedUsersToAdd([]);
    setAvailableUserSearch('');
    loadAvailableUsers();
    setSidebarVisible(true);
  }, [loadAvailableUsers]);

  const handleSaveUser = useCallback(
    async (userData: OrgUnitUserDto) => {
      try {
        setLoading(true);

        // Determine if we're updating an existing user or creating a new one
        const isEditMode = userToEdit !== undefined;

        if (isEditMode) {
          // Update the existing user via API
          await userAccountService.update(userData.userAccount);
        } else {
          // Create a new user via API
          await userAccountService.create(userData.userAccount);
        }
        const updatedOrCreatedUser = await userAccountService.getByEmail(userData.userAccount.email!);

        await orgUnitsService.updateUser(orgUnit.orgUnitId, updatedOrCreatedUser.id as string, {
          losRole: userData.losRole as 'LoanOriginator' | 'Processor' | 'Other' | null | undefined,
          orgUnitId: orgUnit.orgUnitId,
          userId: updatedOrCreatedUser.id as string,
        });

        if (isEditMode) {
          showSuccess(`User ${userData.userAccount.firstName} ${userData.userAccount.lastName} has been updated`);
        } else {
          showSuccess(`User ${userData.userAccount.firstName} ${userData.userAccount.lastName} has been created`);
        }

        // Refresh the available users list
        const allUsers = await userAccountService.getAll();
        setAvailableUsers(allUsers);

        // Reload users list to reflect changes
        loadUsers();

        // Close the user editor sidebar and clear the edit state
        setUserEditorSidebarVisible(false);
        setSidebarVisible(false);
        setUserToEdit(undefined);
      } catch (error: any) {
        console.error(`Error ${userToEdit ? 'updating' : 'creating'} user:`, error);
        showError(error.message);
      } finally {
        setLoading(false);
      }
    },
    [userAccountService, showError, showSuccess, userToEdit, loadUsers]
  );

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  useEffect(() => {
    filterDisplayUsers();
  }, [filterDisplayUsers]);

  useEffect(() => {
    filterAvailableUsers();
  }, [filterAvailableUsers]);

  // Template for user name columns
  const userNameTemplate = (rowData: OrgUnitUserDisplay) => {
    return `${rowData.firstName} ${rowData.lastName}`;
  };

  // Template for the user roles column
  const userRolesTemplate = (rowData: OrgUnitUserDisplay) => {
    return rowData.roles?.join(', ') || '';
  };

  // Template for the actions column
  const actionsTemplate = (rowData: OrgUnitUserDisplay) => {
    return (
      <div className="flex gap-2">
        <Button
          icon="pi pi-pencil"
          className="p-button-rounded p-button-text p-button-primary"
          onClick={() => handleEditUser(rowData)}
        />
        <Button
          icon="pi pi-trash"
          className="p-button-rounded p-button-text p-button-danger"
          onClick={() => handleRemoveUser(rowData)}
        />
      </div>
    );
  };

  // Template for available user name column
  const availableUserNameTemplate = (rowData: UserAccountDto) => {
    return `${rowData.firstName || ''} ${rowData.lastName || ''}`.trim() || 'N/A';
  };

  return (
    <div>
      <Toast ref={toast} />

      <div className="flex justify-content-between align-items-center mb-3">
        <div></div>
        <div className="flex align-items-center gap-2 mr-2">
          <span className="p-input-icon-left p-input-filled">
            <i className="pi pi-search" />
            <InputText
              value={userSearch}
              onChange={(e) => setUserSearch(e.target.value)}
              placeholder="Search users"
              className="w-20rem"
            />
          </span>
          <Button label="Add Users" icon="pi pi-plus" onClick={openAddUserSidebar} disabled={loading} />
        </div>
      </div>

      <DataTable
        value={filteredUserDisplay}
        paginator
        rows={10}
        loading={loading}
        dataKey="userId"
        emptyMessage="No users found"
        className="p-datatable-users"
      >
        <Column field="fullName" header="Name" key="fullName" body={userNameTemplate} sortable />
        <Column field="email" header="Email" key="email" sortable />
        <Column field="roles" header="Roles" key="roles" body={userRolesTemplate} sortable />
        <Column body={actionsTemplate} headerStyle={{ width: '10rem' }} />
      </DataTable>

      <Dialog
        visible={deleteDialogVisible}
        style={{ width: '450px' }}
        header="Confirm"
        modal
        footer={
          <div className="flex justify-content-end gap-2">
            <Button
              label="No"
              icon="pi pi-times"
              className="p-button-text"
              onClick={() => setDeleteDialogVisible(false)}
              disabled={loading}
            />
            <Button
              label="Yes"
              icon="pi pi-check"
              className="p-button-primary"
              onClick={confirmRemoveUser}
              disabled={loading}
            />
          </div>
        }
        onHide={() => setDeleteDialogVisible(false)}
      >
        <div className="flex align-items-center">
          <i className="pi pi-exclamation-triangle mr-3" style={{ fontSize: '2rem' }} />
          <span>
            Are you sure you want to remove <b>{userToDelete ? `${userToDelete.firstName} ${userToDelete.lastName}` : ''}</b>
            ?
          </span>
        </div>
      </Dialog>

      <Sidebar
        visible={sidebarVisible}
        position="right"
        className="p-sidebar-md"
        onHide={() => setSidebarVisible(false)}
        header="Add Users"
        blockScroll
      >
        <BlockUI blocked={loading} className="h-full">
          <div className="p-3">
            <div className="flex justify-content-between align-items-center mb-3">
              <div className="flex gap-2">
                <span className="p-input-icon-left p-input-filled">
                  <i className="pi pi-search" />
                  <InputText
                    value={availableUserSearch}
                    onChange={(e) => setAvailableUserSearch(e.target.value)}
                    placeholder="Search users"
                    className="w-20rem"
                  />
                </span>
              </div>
              <Button
                label="Add New User"
                icon="pi pi-user-plus"
                className="ml-2"
                onClick={() => setUserEditorSidebarVisible(true)}
              />
            </div>

            <DataTable
              value={filteredAvailableUsers}
              selection={selectedUsersToAdd}
              onSelectionChange={(e) => setSelectedUsersToAdd(e.value)}
              paginator
              rows={10}
              selectionMode="multiple"
              dataKey="id"
              className="mb-4"
              emptyMessage="No users available"
            >
              <Column selectionMode="multiple" headerStyle={{ width: '3rem' }} />
              <Column field="firstName" header="Name" body={availableUserNameTemplate} sortable />
              <Column field="email" header="Email" sortable />
            </DataTable>

            <div className="flex justify-content-end gap-2 mt-3">
              <Button
                label="Cancel"
                icon="pi pi-times"
                className="p-button-secondary p-button"
                onClick={() => setSidebarVisible(false)}
                disabled={loading}
              />
              <Button
                label="Add Selected"
                icon="pi pi-check"
                onClick={confirmAddUsers}
                disabled={loading || selectedUsersToAdd.length === 0}
              />
            </div>
          </div>
          {loading && (
            <div className="absolute top-50 left-50 -translate-x-50 -translate-y-50 z-1">
              <ProgressSpinner />
            </div>
          )}
        </BlockUI>
      </Sidebar>

      {/* Sidebar for creating a new user */}
      <Sidebar
        visible={userEditorSidebarVisible}
        position="right"
        className="p-sidebar-lg"
        onHide={() => {
          setUserEditorSidebarVisible(false);
          setUserToEdit(undefined); // Clear the user being edited when closing
        }}
        header={userToEdit ? 'Edit User' : 'Create New User'}
        blockScroll
      >
        <BlockUI blocked={loading} className="h-full">
          <div className="p-3">
            <EditOrgUnitUser
              onSave={handleSaveUser}
              onHide={() => {
                setUserEditorSidebarVisible(false);
                setUserToEdit(undefined); // Clear the user being edited when closing
              }}
              user={userToEdit}
            />
          </div>
          {loading && (
            <div className="absolute top-50 left-50 -translate-x-50 -translate-y-50 z-1">
              <ProgressSpinner style={{ width: '50px', height: '50px' }} strokeWidth="4" animationDuration=".5s" />
            </div>
          )}
        </BlockUI>
      </Sidebar>
    </div>
  );
};
